import Fuse, { FuseResult, IFuseOptions } from 'fuse.js'
import { get, sortBy } from 'lodash'

import { IndustryOrMaterial, Product, Standard, TypeOfTesting } from './catalog'
import { normalizeFormula } from './formatting'
import { ProductFilterType } from './shared-types'
import { standardToString } from './standard'

interface ProductFilterBase {
  type: ProductFilterType
  value: unknown
}

export interface FreeTextFilter extends ProductFilterBase {
  type: ProductFilterType.TEXT
  value: string
}

export interface IndustryOrMaterialFilter extends ProductFilterBase {
  type: ProductFilterType.INDUSTRY_OR_MATERIAL
  value: IndustryOrMaterial | null
}

export interface TypeOfTestingFilter extends ProductFilterBase {
  type: ProductFilterType.TYPE_OF_TESTING
  value: TypeOfTesting | null
}

export interface DeviceFilter extends ProductFilterBase {
  type: ProductFilterType.DEVICE_TYPE
  value: string | null
}

export interface StandardFilter extends ProductFilterBase {
  type: ProductFilterType.STANDARD
  value: string | null
}

export interface ProductFilterValuesByType {
  [ProductFilterType.INDUSTRY_OR_MATERIAL]: IndustryOrMaterial
  [ProductFilterType.TYPE_OF_TESTING]: TypeOfTesting
  [ProductFilterType.DEVICE_TYPE]: string
  [ProductFilterType.STANDARD]: Standard
  [ProductFilterType.TEXT]: string
}

export interface ProductFiltersByType {
  [ProductFilterType.INDUSTRY_OR_MATERIAL]: IndustryOrMaterialFilter
  [ProductFilterType.TYPE_OF_TESTING]: TypeOfTestingFilter
  [ProductFilterType.DEVICE_TYPE]: DeviceFilter
  [ProductFilterType.TEXT]: FreeTextFilter
  [ProductFilterType.STANDARD]: StandardFilter
}

export type ProductFilter = ProductFiltersByType[ProductFilterType]
export type ProductSelectFilter =
  | IndustryOrMaterialFilter
  | TypeOfTestingFilter
  | DeviceFilter
  | StandardFilter

export type SelectInputHandler<F extends ProductSelectFilter> = (
  newValue: F['value'],
) => void

export const freeTextFilter: (value: string) => FreeTextFilter = value => ({
  type: ProductFilterType.TEXT,
  value,
})

export const normalizeMatchString = (s?: string | null) =>
  s ? normalizeFormula(s.replace(/\s/g, '⎵').trim()).toLowerCase() : ''

const filterProduct =
  ({ type, value }: ProductFilter) =>
  (product: Product): boolean => {
    if (type === ProductFilterType.INDUSTRY_OR_MATERIAL) {
      return product[type].some(cat => value === cat)
    }
    if (type === ProductFilterType.TYPE_OF_TESTING) {
      return product[type].some(cat => value === cat)
    }
    if (type === ProductFilterType.STANDARD) {
      return product.standards.some(({ slug }) => value === slug)
    }
    if (type === ProductFilterType.DEVICE_TYPE) {
      return product.deviceTypes.some(
        ({ deviceTypeId, parentDeviceTypeId }) =>
          value === deviceTypeId ||
          (parentDeviceTypeId && value === parentDeviceTypeId),
      )
    }
    if (type === ProductFilterType.TEXT) {
      const normalizedValue = normalizeMatchString(value)
      if (value) {
        return (
          normalizeMatchString(product.name).includes(normalizedValue) ||
          normalizeMatchString(
            product.standards.map(standardToString).join(' '),
          ).includes(normalizedValue)
        )
      }
      return true
    }
    return false
  }

export type FilterableProduct = Product & { standardNames: string[] }

// Fuse scoring: 1 = complete mismatch, 0 = perfect match
const defaultScore = 1

const combineMatches = (
  tagMatches: {
    item: FilterableProduct
    score: number
  }[],
  textMatches: FuseResult<FilterableProduct>[],
) => {
  return tagMatches.filter(tagMatch =>
    textMatches.find(textMatch => tagMatch.item.id === textMatch.item.id),
  )
}

export const filterProducts = ({
  filters: { text, ...tags },
  fuse,
  products,
}: {
  filters: Partial<ProductFiltersByType>
  fuse: Fuse<FilterableProduct>
  products: FilterableProduct[]
}) => {
  const tagFilters = Object.values(tags)
    .filter(filter => filter && filter.value)
    .map(filterProduct)

  const textQuery = text?.value.trim()

  if (tagFilters.length === 0 && !textQuery) {
    return products
  }

  const textMatches = textQuery ? fuse.search(textQuery) : []

  const tagMatches = products
    .filter(item => tagFilters.every(f => f(item)))
    .map(item => ({ item, score: defaultScore }))

  const combinedMatches: { item: FilterableProduct; score?: number }[] =
    (() => {
      if (tagFilters.length) {
        if (textQuery) {
          return combineMatches(tagMatches, textMatches)
        }
        return tagMatches
      }
      return textMatches
    })()

  return sortBy(combinedMatches, [
    'score',
    ({ item }) => item.popularityRank,
    ({ item }) => item.name,
  ]).map(cm => cm.item)
}

export const productFilterFuseOptions: IFuseOptions<FilterableProduct> = {
  getFn: (obj, path) => {
    return normalizeFormula((get(obj, path, '') ?? '') as string)
  },
  ignoreLocation: true, // default of false did not find even exact matches towards end of long product names
  includeScore: true, // needed for manual score adjustment
  keys: [
    `name`,
    `descriptionRaw`,
    `metaDescription`,
    `sampleRequirements`,
    'standardNames',
  ],
  shouldSort: false, // only sorted after score has been combined with tag search score
  threshold: 0.2,
  useExtendedSearch: true,
}
